import { Check, ChevronDown, Plus, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { usePipelinePositions } from "../../contexts/PipelineContext";
import { mapPipelineName, mapSource } from "../../utils/valueMap";

export type InboxFilterState = {
  pipelineId?: string;
  pipelinePositionId?: string;
  employeeId?: string;
  source?: string;
  tagIds?: string[];
};

type Pipeline = { id: string; name: string };
type Employee = { id: string; name: string };
type Tag = { id: string; name: string };
type FilterKey = "pipeline" | "position" | "source" | "employee" | "tags";

type InboxFiltersProps = {
  search: string;
  onSearchChange: (value: string) => void;
  filters: InboxFilterState;
  onFiltersChange: (next: Partial<InboxFilterState>) => void;
  pipelines: Pipeline[];
  employees: Employee[];
  tags: Tag[];
  showEmployeeFilter: boolean;
  isSearching?: boolean;
  onNewConversation?: () => void;
};

const SEARCH_DEBOUNCE_MS = 500;

const FILTER_LABELS: Record<FilterKey, string> = {
  pipeline: "Embudo",
  position: "Etapa",
  source: "Fuente",
  employee: "Asesor",
  tags: "Etiqueta",
};

const InboxFilters = ({
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  pipelines,
  employees,
  tags,
  showEmployeeFilter,
  isSearching,
  onNewConversation,
}: InboxFiltersProps): JSX.Element => {
  const positionsForSelectedPipeline = usePipelinePositions(filters.pipelineId);

  const isMobile = useIsMobile();
  const [searchInput, setSearchInput] = useState(search);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const [openKey, setOpenKey] = useState<FilterKey | null>(null);
  const [employeeSearch, setEmployeeSearch] = useState("");

  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const commitSearch = (value: string) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearchChange(value), SEARCH_DEBOUNCE_MS);
  };

  const handleSearchInput = (value: string) => {
    setSearchInput(value);
    commitSearch(value);
  };

  const handleSearchClear = () => {
    clearTimeout(debounceRef.current);
    setSearchInput("");
    onSearchChange("");
  };

  const closePicker = () => {
    setOpenKey(null);
    setEmployeeSearch("");
  };

  const openPicker = (key: FilterKey) => {
    setEmployeeSearch("");
    setOpenKey(key);
  };

  const clearFilter = (key: FilterKey) => {
    if (key === "pipeline") {
      onFiltersChange({ pipelineId: undefined, pipelinePositionId: undefined });
    } else if (key === "position") {
      onFiltersChange({ pipelinePositionId: undefined });
    } else if (key === "source") {
      onFiltersChange({ source: undefined });
    } else if (key === "tags") {
      onFiltersChange({ tagIds: undefined });
    } else {
      onFiltersChange({ employeeId: undefined });
    }
  };

  const selectOption = (key: FilterKey, value: string) => {
    if (key === "pipeline") {
      onFiltersChange({ pipelineId: value, pipelinePositionId: undefined });
    } else if (key === "position") {
      onFiltersChange({ pipelinePositionId: value });
    } else if (key === "source") {
      onFiltersChange({ source: value });
    } else {
      onFiltersChange({ employeeId: value });
    }
    closePicker();
  };

  const toggleTag = (id: string) => {
    const current = filters.tagIds ?? [];
    const next = current.includes(id) ? current.filter((v) => v !== id) : [...current, id];
    onFiltersChange({ tagIds: next.length ? next : undefined });
  };

  const positionOptions = useMemo(
    () => (filters.pipelineId ? positionsForSelectedPipeline : []),
    [filters.pipelineId, positionsForSelectedPipeline]
  );

  const pipelineLabel = useMemo(() => {
    if (!filters.pipelineId) return null;
    return (
      mapPipelineName[filters.pipelineId as keyof typeof mapPipelineName] ||
      pipelines.find((p) => p.id === filters.pipelineId)?.name ||
      filters.pipelineId
    );
  }, [filters.pipelineId, pipelines]);

  const positionLabel = useMemo(() => {
    if (!filters.pipelinePositionId) return null;
    return (
      positionsForSelectedPipeline.find((p) => p.id === filters.pipelinePositionId)
        ?.displayName ?? filters.pipelinePositionId
    );
  }, [filters.pipelinePositionId, positionsForSelectedPipeline]);

  const employeeLabel = useMemo(() => {
    if (!filters.employeeId) return null;
    return employees.find((e) => e.id === filters.employeeId)?.name ?? filters.employeeId;
  }, [filters.employeeId, employees]);

  const sourceLabel = useMemo(() => {
    if (!filters.source) return null;
    return mapSource[filters.source as keyof typeof mapSource] ?? filters.source;
  }, [filters.source]);

  const tagsLabel = useMemo(() => {
    const ids = filters.tagIds ?? [];
    if (ids.length === 0) return null;
    if (ids.length > 1) return `${ids.length} seleccionadas`;
    return tags.find((t) => t.id === ids[0])?.name ?? ids[0];
  }, [filters.tagIds, tags]);

  const valueLabelFor = (key: FilterKey): string | null => {
    if (key === "pipeline") return pipelineLabel;
    if (key === "position") return positionLabel;
    if (key === "source") return sourceLabel;
    if (key === "tags") return tagsLabel;
    return employeeLabel;
  };

  const filterKeys: FilterKey[] = [
    ...(showEmployeeFilter
      ? (["pipeline", "position", "source", "employee"] as const)
      : (["pipeline", "position", "source"] as const)),
    ...(tags.length > 0 ? (["tags"] as const) : []),
  ];

  const renderOptionList = (key: FilterKey) => {
    let items: { id: string; label: string }[] = [];
    let selected: string | undefined;
    let allowSearch = false;

    if (key === "pipeline") {
      items = pipelines.map((p) => ({
        id: p.id,
        label: mapPipelineName[p.id as keyof typeof mapPipelineName] || p.name,
      }));
      selected = filters.pipelineId;
    } else if (key === "position") {
      items = positionOptions.map((p) => ({ id: p.id, label: p.displayName }));
      selected = filters.pipelinePositionId;
    } else if (key === "source") {
      items = Object.entries(mapSource).map(([id, label]) => ({ id, label }));
      selected = filters.source;
      allowSearch = items.length > 5;
    } else if (key === "tags") {
      items = tags.map((t) => ({ id: t.id, label: t.name }));
      allowSearch = items.length > 5;
    } else {
      items = employees.map((e) => ({ id: e.id, label: e.name }));
      selected = filters.employeeId;
      allowSearch = items.length > 5;
    }

    if (items.length === 0) {
      return (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          No hay opciones disponibles
        </p>
      );
    }

    const filtered = allowSearch
      ? items.filter((i) =>
          i.label.toLowerCase().includes(employeeSearch.toLowerCase())
        )
      : items;

    return (
      <div className="flex flex-col py-1">
        {allowSearch && (
          <div className="px-4 pb-2">
            <div className="relative">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              />
              <input
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
                placeholder={
                  key === "source"
                    ? "Buscar fuente"
                    : key === "tags"
                      ? "Buscar etiqueta"
                      : "Buscar asesor"
                }
                className="h-9 w-full rounded-lg border border-input bg-white pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary"
              />
            </div>
          </div>
        )}
        {filtered.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            No hay coincidencias
          </p>
        ) : (
          filtered.map((item) => {
            const isSelected =
              key === "tags" ? !!filters.tagIds?.includes(item.id) : selected === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => (key === "tags" ? toggleTag(item.id) : selectOption(key, item.id))}
                className={cn(
                  "flex min-h-11 items-center gap-3 px-4 text-left text-sm transition-colors hover:bg-muted active:bg-muted",
                  isSelected && "text-palomma-primary"
                )}
              >
                <Check
                  className={cn(
                    "size-4 shrink-0",
                    isSelected ? "opacity-100" : "opacity-0"
                  )}
                  aria-hidden="true"
                />
                <span className="flex-1 truncate">{item.label}</span>
              </button>
            );
          })
        )}
      </div>
    );
  };

  const renderChip = (key: FilterKey): ReactNode => {
    const value = valueLabelFor(key);
    const label = FILTER_LABELS[key];
    const isOpen = openKey === key;
    const disabled = key === "position" && !filters.pipelineId;

    if (value) {
      return (
        <span
          key={key}
          className={cn(
            "inline-flex min-h-8 shrink-0 items-stretch overflow-hidden rounded-full border border-border bg-white text-xs font-medium text-palomma-secondary",
            isOpen && "border-palomma-primary"
          )}
        >
          <button
            type="button"
            onClick={() => openPicker(key)}
            className="inline-flex items-center gap-1 pl-2.5 pr-1.5 transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-palomma-primary"
          >
            <span className="text-muted-foreground">{label}:</span>
            <span className="max-w-[140px] truncate">{value}</span>
          </button>
          <button
            type="button"
            onClick={() => clearFilter(key)}
            aria-label={`Quitar filtro ${label}`}
            className="inline-flex items-center border-l border-border px-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-palomma-secondary focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-palomma-primary"
          >
            <X className="size-3" aria-hidden="true" />
          </button>
        </span>
      );
    }

    return (
      <button
        key={key}
        type="button"
        onClick={() => openPicker(key)}
        disabled={disabled}
        title={disabled ? "Selecciona un embudo primero" : undefined}
        className={cn(
          "inline-flex min-h-8 shrink-0 items-center gap-1 rounded-full border border-dashed border-border bg-white px-2.5 text-xs font-medium text-muted-foreground transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-palomma-primary",
          !disabled &&
            "hover:border-foreground/30 hover:text-palomma-secondary",
          disabled && "cursor-not-allowed opacity-50",
          isOpen && "border-palomma-primary text-palomma-secondary"
        )}
      >
        <Plus className="size-3" aria-hidden="true" />
        <span>{label}</span>
        <ChevronDown className="size-3 opacity-60" aria-hidden="true" />
      </button>
    );
  };

  const popoverKey = !isMobile ? openKey : null;
  const sheetKey = isMobile ? openKey : null;
  const sheetTitle = sheetKey ? FILTER_LABELS[sheetKey] : "";

  return (
    <div className="border-b border-border">
      {/* Search owns its own row. Sharing one with "Nueva conversación" cost the
          input ~170px to a button whose label cannot shrink, which left it
          narrower than its own placeholder once the list panel gave width back to
          the conversation column. Stacking is unconditional: the alternative was a
          breakpoint that would have to stay in sync with the container's own
          three-column threshold. */}
      <div className="flex flex-col gap-2 pt-1 pb-2">
        <div className="relative w-full">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            spellCheck={false}
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape" && searchInput) {
                e.preventDefault();
                handleSearchClear();
              }
            }}
            aria-label="Buscar conversaciones por nombre, teléfono o usuario"
            placeholder="Buscar por nombre, teléfono o usuario"
            className={cn(
              "h-10 w-full rounded-lg border border-input bg-white pl-9 text-base placeholder:text-muted-foreground",
              "focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary",
              "md:h-9 md:text-sm",
              searchInput ? "pr-10" : "pr-3",
              "[&::-webkit-search-cancel-button]:appearance-none"
            )}
          />
          {searchInput && (
            <button
              type="button"
              onClick={handleSearchClear}
              aria-label="Limpiar búsqueda"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-palomma-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-palomma-primary"
            >
              <X className="size-3.5" />
            </button>
          )}
          {isSearching && searchInput && (
            <span
              aria-hidden="true"
              className="absolute right-9 top-1/2 size-3.5 -translate-y-1/2 animate-spin rounded-full border-2 border-muted border-t-palomma-primary motion-reduce:animate-none"
            />
          )}
        </div>
        {onNewConversation && (
          <Button
            variant="main"
            onClick={onNewConversation}
            className="h-10 w-full gap-1.5 md:h-9"
          >
            <Plus className="size-4" aria-hidden="true" />
            <span>Nueva conversación</span>
          </Button>
        )}
      </div>

      <Popover
        open={popoverKey !== null}
        onOpenChange={(open) => !open && closePicker()}
      >
        <div className="flex flex-wrap items-center gap-1.5 pb-2.5">
          {filterKeys.map((key) =>
            popoverKey === key ? (
              <PopoverAnchor key={key} asChild>
                {renderChip(key) as JSX.Element}
              </PopoverAnchor>
            ) : (
              renderChip(key)
            )
          )}
        </div>

        <PopoverContent align="start" sideOffset={6} className="w-60 p-0">
          {popoverKey && (
            <>
              <div className="border-b border-border px-4 py-2.5">
                <span className="text-sm font-semibold text-palomma-secondary">
                  {FILTER_LABELS[popoverKey]}
                </span>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {renderOptionList(popoverKey)}
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>

      <Sheet
        open={sheetKey !== null}
        onOpenChange={(open) => !open && closePicker()}
      >
        <SheetContent side="bottom" className="flex max-h-[70dvh] flex-col gap-0 rounded-t-2xl p-0">
          <div className="border-b border-border px-4 py-3 pr-12">
            <SheetTitle className="text-sm font-semibold text-palomma-secondary">
              {sheetTitle}
            </SheetTitle>
          </div>
          <SheetDescription className="sr-only">
            Selecciona una opción para filtrar las conversaciones.
          </SheetDescription>
          <div className="overflow-y-auto pb-20">
            {sheetKey && renderOptionList(sheetKey)}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default InboxFilters;
